import { useCallback, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CheckCircle2, XCircle } from "lucide-react";
import api from "../../services/api";
import { useToast } from "../../contexts/toast/toast-context";
import { LoadingButton } from "../button/loading-button";
import { formatHour, getApiErrorMessage } from "../../utils";
import type { Tenant } from "../../types/tenant";
import type { Appointment, AppointmentStatus } from "../../types/appointment";
import {
  formatDateKey,
  isDateSelectableForTenant,
} from "../../utils/appointment";

interface ConversationScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenant: Tenant;
  userId: string;
  conversationId?: string;
  onScheduled?: () => void;
}

type FindAllAppointmentDto = {
  data: Appointment[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

const STATUS_META: Record<
  AppointmentStatus,
  {
    label: string;
    dot: string;
    text: string;
  }
> = {
  SCHEDULED: {
    label: "Agendado",
    dot: "bg-amber-500",
    text: "text-amber-600",
  },
  CANCELED: {
    label: "Cancelado",
    dot: "bg-rose-500",
    text: "text-rose-600",
  },
};

const CONFIRMED_META = {
  label: "Confirmado",
  dot: "bg-emerald-500",
  text: "text-emerald-600",
} as const;

const resolveSortTimestamp = (appointment: Appointment) => {
  const candidates = [
    appointment.updatedAt,
    appointment.createdAt,
    appointment.scheduledAt,
  ];

  for (const value of candidates) {
    if (!value) continue;
    const parsed = new Date(value).getTime();
    if (!Number.isNaN(parsed)) return parsed;
  }

  return 0;
};

const isBlockingAppointment = (appointment: Appointment) => {
  if (appointment.status === "CANCELED") return false;

  const scheduledAt = new Date(appointment.scheduledAt).getTime();
  if (Number.isNaN(scheduledAt)) return false;

  return scheduledAt >= Date.now();
};

export function ConversationScheduleModal({
  isOpen,
  onClose,
  tenant,
  userId,
  conversationId,
  onScheduled,
}: ConversationScheduleModalProps) {
  const { error, info, success } = useToast();

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [isLoadingTimes, setIsLoadingTimes] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [linkedAppointment, setLinkedAppointment] =
    useState<Appointment | null>(null);
  const [isLoadingLinkedAppointment, setIsLoadingLinkedAppointment] =
    useState(false);
  const [hasCheckedLinkedAppointment, setHasCheckedLinkedAppointment] =
    useState(false);
  const [statusUpdating, setStatusUpdating] = useState<
    "confirm" | "cancel" | null
  >(null);

  const isDateSelectable = useCallback(
    (date: Date) => isDateSelectableForTenant(date, tenant),
    [tenant],
  );

  const fetchLinkedAppointment = useCallback(async () => {
    if (!isOpen || !userId) return;

    setHasCheckedLinkedAppointment(false);
    setIsLoadingLinkedAppointment(true);
    try {
      const response = await api.get<FindAllAppointmentDto>("/scheduling", {
        params: {
          page: 1,
          limit: 200,
          userId,
        },
      });

      const appointments = response.data.data.filter((appointment) =>
        conversationId
          ? appointment.conversationId === conversationId
          : appointment.user?.id === userId,
      );

      const latestBlockingAppointment = [...appointments]
        .filter(isBlockingAppointment)
        .sort((a, b) => resolveSortTimestamp(b) - resolveSortTimestamp(a))[0];

      setLinkedAppointment(latestBlockingAppointment ?? null);
    } catch (err) {
      error(getApiErrorMessage(err));
      setLinkedAppointment(null);
    } finally {
      setIsLoadingLinkedAppointment(false);
      setHasCheckedLinkedAppointment(true);
    }
  }, [conversationId, error, isOpen, userId]);

  const fetchAvailableTimes = useCallback(async () => {
    if (
      !isOpen ||
      !hasCheckedLinkedAppointment ||
      linkedAppointment ||
      isLoadingLinkedAppointment
    )
      return;
    setIsLoadingTimes(true);
    try {
      const response = await api.get<string[]>("/scheduling/available-times", {
        params: {
          date: formatDateKey(selectedDate),
        },
      });

      setAvailableTimes(response.data);
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsLoadingTimes(false);
    }
  }, [
    error,
    hasCheckedLinkedAppointment,
    isLoadingLinkedAppointment,
    isOpen,
    linkedAppointment,
    selectedDate,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    fetchLinkedAppointment();
  }, [fetchLinkedAppointment, isOpen]);

  useEffect(() => {
    if (!isOpen || !hasCheckedLinkedAppointment) return;
    fetchAvailableTimes();
  }, [fetchAvailableTimes, hasCheckedLinkedAppointment, isOpen]);

  useEffect(() => {
    if (!isOpen || linkedAppointment) return;
    setSelectedTime("");
  }, [linkedAppointment, selectedDate, isOpen]);

  useEffect(() => {
    if (isOpen) return;
    setSelectedTime("");
    setAdditionalInfo("");
    setAvailableTimes([]);
    setSelectedDate(new Date());
    setLinkedAppointment(null);
    setIsLoadingLinkedAppointment(false);
    setHasCheckedLinkedAppointment(false);
    setStatusUpdating(null);
  }, [isOpen]);

  const handleSchedule = async () => {
    if (!userId) {
      info("Contato inválido para agendamento.");
      return;
    }

    if (!selectedTime) {
      info("Selecione um horário disponível.");
      return;
    }

    const [hours, minutes] = selectedTime.split(":").map(Number);
    const scheduledAt = new Date(selectedDate);
    scheduledAt.setHours(hours, minutes, 0, 0);

    setIsSaving(true);

    try {
      await api.post("/scheduling", {
        userId,
        scheduledAt: scheduledAt.toISOString(),
        conversationId,
        additionalInfo: additionalInfo.trim() || undefined,
      });

      success("Agendamento criado com sucesso!");
      onClose();
      onScheduled?.();
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!linkedAppointment) return;

    setStatusUpdating("confirm");
    try {
      const response = await api.patch<Appointment>(
        `/scheduling/${linkedAppointment.id}/confirm`,
      );
      setLinkedAppointment(response.data);
      success("Status atualizado com sucesso!");
      onScheduled?.();
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setStatusUpdating(null);
    }
  };

  const handleCancel = async () => {
    if (!linkedAppointment) return;

    setStatusUpdating("cancel");
    try {
      const response = await api.patch<Appointment>(
        `/scheduling/${linkedAppointment.id}/cancel`,
      );
      setLinkedAppointment(response.data);
      success("Status atualizado com sucesso!");
      onScheduled?.();
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setStatusUpdating(null);
    }
  };

  if (!isOpen) return null;

  const isUpdatingStatus = statusUpdating !== null;
  const isConfirming = statusUpdating === "confirm";
  const isCanceling = statusUpdating === "cancel";
  const isBusy = isSaving || isUpdatingStatus;
  const appointmentMeta = linkedAppointment
    ? linkedAppointment.status === "CANCELED"
      ? STATUS_META.CANCELED
      : linkedAppointment.confirmed
        ? CONFIRMED_META
        : STATUS_META.SCHEDULED
    : null;
  const appointmentDate = linkedAppointment
    ? new Date(linkedAppointment.scheduledAt)
    : null;
  const hasValidAppointmentDate = appointmentDate
    ? !Number.isNaN(appointmentDate.getTime())
    : false;
  const formattedDate = hasValidAppointmentDate
    ? appointmentDate.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "--";
  const formattedTime = hasValidAppointmentDate
    ? formatHour(appointmentDate)
    : "--";
  const confirmedAt = linkedAppointment?.confirmedAt
    ? new Date(linkedAppointment.confirmedAt)
    : null;
  const hasValidConfirmedAt =
    confirmedAt && !Number.isNaN(confirmedAt.getTime());
  const confirmedAtDate = hasValidConfirmedAt ? (confirmedAt as Date) : null;
  const trimmedAdditionalInfo = linkedAppointment?.additionalInfo?.trim();

  return (
    <div
      className="modal-overlay"
      onClick={() => {
        if (!isBusy) onClose();
      }}
    >
      <div
        className="modal-card-lg space-y-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-lg font-semibold text-slate-800">
            {linkedAppointment ? "Agendamento vinculado" : "Agendar consulta"}
          </h2>
          <p className="text-sm text-slate-500">
            {linkedAppointment
              ? "Este atendimento já possui agendamento associado."
              : "Selecione a data e o horário disponível."}
          </p>
        </div>

        {isLoadingLinkedAppointment ? (
          <div className="app-card p-4 text-sm text-slate-500">
            Carregando agendamento vinculado...
          </div>
        ) : linkedAppointment && appointmentMeta ? (
          <>
            <div className="rounded-lg border border-slate-100 bg-slate-50/70 px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <span
                  className={`h-3 w-3 rounded-full ${appointmentMeta.dot}`}
                  aria-hidden
                />
                <span>
                  {formattedDate} às {formattedTime}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                <span className={appointmentMeta.text}>
                  {appointmentMeta.label}
                </span>
                <span className="text-slate-300">|</span>
                <span>
                  {linkedAppointment.agentType === "AI"
                    ? "Agendado por IA"
                    : "Agendado pelo atendente"}
                </span>
                {confirmedAtDate && (
                  <>
                    <span className="text-slate-300">|</span>
                    <span>
                      Confirmado em {confirmedAtDate.toLocaleString("pt-BR")}
                    </span>
                  </>
                )}
              </div>
              {trimmedAdditionalInfo && (
                <p className="mt-3 text-xs text-slate-600">
                  {trimmedAdditionalInfo}
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3">
              <button
                onClick={onClose}
                className="btn btn-secondary"
                disabled={isUpdatingStatus}
              >
                Fechar
              </button>
              <LoadingButton
                onClick={handleConfirm}
                className="btn-link-success flex items-center gap-1 transition disabled:opacity-60"
                title="Confirmar agendamento"
                disabled={
                  isUpdatingStatus ||
                  linkedAppointment.status !== "SCHEDULED" ||
                  linkedAppointment.confirmed
                }
                isLoading={isConfirming}
                loadingLabel="Confirmando"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-xs font-medium">Confirmar</span>
              </LoadingButton>
              <LoadingButton
                onClick={handleCancel}
                className="btn-link-danger flex items-center gap-1 transition disabled:opacity-60"
                title="Cancelar agendamento"
                disabled={
                  isUpdatingStatus || linkedAppointment.status === "CANCELED"
                }
                isLoading={isCanceling}
                loadingLabel="Cancelando"
              >
                <XCircle className="w-4 h-4" />
                <span className="text-xs font-medium">Cancelar</span>
              </LoadingButton>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-4">
              <div className="app-card p-4">
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => date && setSelectedDate(date)}
                  inline
                  calendarStartDay={1}
                  filterDate={isDateSelectable}
                />
              </div>

              <div>
                <label className="form-label">Horário disponível</label>
                <select
                  className="form-input mt-2"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                >
                  <option value="">
                    {isLoadingTimes
                      ? "Carregando horários..."
                      : availableTimes.length
                        ? "Selecione um horário"
                        : "Nenhum horário disponível"}
                  </option>
                  {availableTimes.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Informações adicionais</label>
                <textarea
                  className="form-textarea mt-2"
                  rows={3}
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                  placeholder="Ex.: observações relevantes para o atendimento."
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={onClose}
                className="btn btn-secondary"
                disabled={isSaving}
              >
                Cancelar
              </button>
              <LoadingButton
                onClick={handleSchedule}
                className="btn btn-neutral"
                isLoading={isSaving}
                loadingLabel="Agendando"
              >
                Agendar consulta
              </LoadingButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
